import { Extension, type CommandProps } from "@tiptap/core";
import {
  Fragment,
  type Node as ProseMirrorNode,
  type NodeRange,
} from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";
import { liftListItem, sinkListItem } from "@tiptap/pm/schema-list";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    indent: {
      indent: () => ReturnType;
      outdent: () => ReturnType;
    };
  }
}

const INDENT_TYPES = ["paragraph", "heading"];
const LIST_ITEM = "listItem";
const MIN_INDENT = 0;
const MAX_INDENT = 6;

function isInsideListItem(doc: ProseMirrorNode, pos: number) {
  const $pos = doc.resolve(Math.min(pos + 1, doc.content.size));
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    if ($pos.node(depth).type.name === "listItem") return true;
  }
  return false;
}

function collectTargets(state: CommandProps["state"]) {
  const { selection } = state;
  const targets: { pos: number; node: ProseMirrorNode }[] = [];
  const seen = new Set<number>();

  const add = (node: ProseMirrorNode, pos: number) => {
    if (!INDENT_TYPES.includes(node.type.name) || seen.has(pos)) return;
    if (isInsideListItem(state.doc, pos)) return;
    seen.add(pos);
    targets.push({ pos, node });
  };

  if (selection.empty) {
    const { $from } = selection;
    for (let depth = $from.depth; depth > 0; depth -= 1) {
      const node = $from.node(depth);
      if (INDENT_TYPES.includes(node.type.name)) {
        add(node, $from.before(depth));
        break;
      }
    }
    return targets;
  }

  state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
    add(node, pos);
  });

  return targets;
}

const ABSORBABLE = new Set(["paragraph", "heading"]);

function overlapsSelection(
  state: CommandProps["state"],
  from: number,
  to: number
) {
  const { from: selFrom, to: selTo } = state.selection;
  return selFrom < to && selTo > from;
}

function listItemBlockRange(state: CommandProps["state"]) {
  const itemType = state.schema.nodes.listItem;
  if (!itemType) return null;
  const { $from, $to } = state.selection;
  const range = $from.blockRange(
    $to,
    (node) => node.childCount > 0 && node.firstChild?.type === itemType
  );
  return range ? { range, itemType } : null;
}

function selectionInListItem(state: CommandProps["state"]) {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if ($from.node(depth).type.name === "listItem") return true;
  }
  return false;
}

/**
 * Indent the first items of a list into the previous list of the same type.
 * Paragraphs and headings between the lists move only when the selection
 * covers them. An unselected block stops the join, and the caller indents
 * just the selected items instead.
 */
function nestListPrefixIntoPrevious(props: CommandProps, range: NodeRange) {
  const { state } = props;
  const list = range.parent;
  const listType = list.type;
  const listPos = range.start - 1;
  const $atList = state.doc.resolve(listPos);
  const parent = $atList.parent;
  const listIndex = $atList.index();

  const absorb: ProseMirrorNode[] = [];
  let targetIndex = -1;
  let pos = listPos;
  for (let i = listIndex - 1; i >= 0; i -= 1) {
    const child = parent.child(i);
    pos -= child.nodeSize;
    if (child.type === listType) {
      targetIndex = i;
      break;
    }
    if (
      ABSORBABLE.has(child.type.name) &&
      overlapsSelection(state, pos, pos + child.nodeSize)
    ) {
      absorb.unshift(child);
      continue;
    }
    return false;
  }
  if (targetIndex < 0) return false;

  const targetList = parent.child(targetIndex);
  const lastItem = targetList.lastChild;
  if (!lastItem || lastItem.type.name !== "listItem") return false;

  const selected: ProseMirrorNode[] = [];
  for (let i = range.startIndex; i < range.endIndex; i += 1) {
    selected.push(list.child(i));
  }
  if (selected.length === 0) return false;

  const nestedChild = lastItem.lastChild;
  const joinNested = nestedChild?.type === listType;
  const nextChildren: ProseMirrorNode[] = [];
  lastItem.forEach((child, _offset, index) => {
    if (joinNested && index === lastItem.childCount - 1) return;
    nextChildren.push(child);
  });
  nextChildren.push(...absorb);
  if (joinNested && nestedChild) {
    nextChildren.push(
      nestedChild.copy(nestedChild.content.append(Fragment.from(selected)))
    );
  } else {
    nextChildren.push(listType.create(list.attrs, selected));
  }
  if (!lastItem.type.validContent(Fragment.from(nextChildren))) return false;
  if (!props.dispatch) return true;

  const { tr } = props;
  let targetPos = listPos;
  for (let i = listIndex - 1; i >= targetIndex; i -= 1) {
    targetPos -= parent.child(i).nodeSize;
  }
  let lastItemPos = targetPos + 1;
  for (let i = 0; i < targetList.childCount - 1; i += 1) {
    lastItemPos += targetList.child(i).nodeSize;
  }

  const takeWholeList = range.endIndex === list.childCount;
  if (takeWholeList) {
    tr.delete(listPos, listPos + list.nodeSize);
  } else {
    tr.delete(range.start, range.end);
  }

  let absorbFrom = listPos;
  for (const node of absorb) absorbFrom -= node.nodeSize;
  if (absorb.length) tr.delete(absorbFrom, listPos);

  const absorbSize = absorb.reduce((size, node) => size + node.nodeSize, 0);
  let cursorPos: number;
  if (joinNested && nestedChild) {
    const nestedPos =
      lastItemPos + lastItem.nodeSize - 1 - nestedChild.nodeSize;
    if (absorb.length) tr.insert(nestedPos, Fragment.from(absorb));
    const nestedStart = tr.mapping.map(nestedPos);
    cursorPos = nestedStart + nestedChild.nodeSize - 1;
    tr.insert(cursorPos, Fragment.from(selected));
  } else {
    const insertAt = lastItemPos + lastItem.nodeSize - 1;
    tr.insert(
      insertAt,
      Fragment.from([...absorb, listType.create(list.attrs, selected)])
    );
    cursorPos = insertAt + absorbSize + 1;
  }

  tr.setSelection(TextSelection.near(tr.doc.resolve(cursorPos + 1)));
  props.dispatch(tr.scrollIntoView());
  return true;
}

function clampIndent(level: number) {
  return Math.min(MAX_INDENT, Math.max(MIN_INDENT, level));
}

/**
 * Shift list items with a margin when they cannot nest. The first item of a
 * list has no previous sibling, and there is not always an earlier list to
 * join, but the toolbar should still move that bullet inward.
 */
function changeListItemIndent(
  props: CommandProps,
  range: NodeRange,
  delta: number
) {
  const list = range.parent;
  const updates: { pos: number; node: ProseMirrorNode; next: number }[] = [];
  let pos = range.start;
  for (let index = range.startIndex; index < range.endIndex; index += 1) {
    const node = list.child(index);
    if (node.type.name === LIST_ITEM) {
      const current = Number(node.attrs.indent) || 0;
      const next = clampIndent(current + delta);
      if (next !== current) updates.push({ pos, node, next });
    }
    pos += node.nodeSize;
  }

  if (updates.length === 0) return false;
  if (!props.dispatch) return true;

  updates.forEach(({ pos, node, next }) => {
    props.tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
  });
  props.dispatch(props.tr);
  return true;
}

function indentList(props: CommandProps) {
  const found = listItemBlockRange(props.state);
  if (!found) return false;
  if (found.range.startIndex > 0) {
    return sinkListItem(found.itemType)(props.state, props.dispatch);
  }
  if (nestListPrefixIntoPrevious(props, found.range)) return true;
  return changeListItemIndent(props, found.range, 1);
}

function changeIndent({ state, tr, dispatch }: CommandProps, delta: number) {
  const updates = collectTargets(state).flatMap(({ pos, node }) => {
    const current = Number(node.attrs.indent) || 0;
    const next = clampIndent(current + delta);
    if (next === current) return [];
    return [{ pos, node, next }];
  });

  if (updates.length === 0) return false;
  // `can()` calls this with no dispatch. Leave the live transaction alone then.
  if (!dispatch) return true;

  updates.forEach(({ pos, node, next }) => {
    tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
  });
  dispatch(tr);
  return true;
}

export const Indent = Extension.create({
  name: "indent",

  addGlobalAttributes() {
    return [
      {
        types: [...INDENT_TYPES, LIST_ITEM],
        attributes: {
          indent: {
            default: 0,
            keepOnSplit: true,
            parseHTML: (element) => {
              const level = Number.parseInt(
                element.getAttribute("data-indent") || "0",
                10
              );
              if (Number.isNaN(level)) return 0;
              return Math.min(MAX_INDENT, Math.max(MIN_INDENT, level));
            },
            renderHTML: (attributes) => {
              const level = Number(attributes.indent) || 0;
              if (level <= 0) return {};
              return { "data-indent": String(level) };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indent:
        () =>
        (props) => {
          if (selectionInListItem(props.state)) {
            return indentList(props);
          }
          return changeIndent(props, 1);
        },
      outdent:
        () =>
        (props) => {
          const listItem = props.state.schema.nodes.listItem;
          if (listItem && selectionInListItem(props.state)) {
            const found = listItemBlockRange(props.state);
            if (
              found &&
              found.range.startIndex === 0 &&
              changeListItemIndent(props, found.range, -1)
            ) {
              return true;
            }
            return liftListItem(listItem)(props.state, props.dispatch);
          }
          return changeIndent(props, -1);
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        if (this.editor.isActive("codeBlock")) return false;
        return this.editor.commands.indent();
      },
      "Shift-Tab": () => {
        if (this.editor.isActive("codeBlock")) return false;
        return this.editor.commands.outdent();
      },
    };
  },
});
